import { useState, useRef } from "react";
import type { Category, Priority } from "../../../../shared/types";
import { Overlay } from "../../../../ui/atoms/Overlay";
import { useCategories } from "../../../../shared/stores/category-store";
import { PRIORITY_LABELS } from "../../../../shared/types";

interface AddTaskInlineDialogProps {
  readonly open: boolean;
  readonly defaultCategory: Category;
  readonly onConfirm: (title: string, category: Category, priority: Priority) => void;
  readonly onCancel: () => void;
}

function AddTaskInlineForm({
  defaultCategory,
  onConfirm,
  onCancel,
}: Omit<AddTaskInlineDialogProps, "open">) {
  const [title, setTitle] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<Category>(defaultCategory);
  const [selectedPriority, setSelectedPriority] = useState<Priority>("normal");
  const inputRef = useRef<HTMLInputElement>(null);

  const { categories, labels, dotColors } = useCategories();

  function handleSubmit() {
    const trimmed = title.trim();
    if (trimmed) onConfirm(trimmed, selectedCategory, selectedPriority);
    else onCancel();
  }

  return (
    <div className="bg-surface border border-border rounded-2xl shadow-xl p-5 w-80 max-w-[90vw] flex flex-col gap-4">
      <h2 className="text-sm font-semibold text-ink">Nouvelle tâche</h2>

      {/* Titre */}
      <input
        ref={inputRef}
        type="text"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") handleSubmit();
        }}
        placeholder="Titre de la tâche..."
        className="w-full px-3 py-2 text-xs text-ink bg-surface-alt border border-border/60 rounded-md focus:outline-none focus:border-accent/60"
        autoFocus
      />

      {/* Catégorie */}
      <div>
        <span className="text-[10px] text-ink-faint uppercase tracking-wide mb-1.5 block">
          Catégorie
        </span>
        <div className="flex gap-1.5 flex-wrap">
          {categories.map((cat) => (
            <button
              key={cat}
              type="button"
              onClick={() => setSelectedCategory(cat as Category)}
              className={`flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-medium rounded-md transition-colors ${
                selectedCategory === cat
                  ? "bg-accent text-white"
                  : "text-ink-muted hover:text-ink hover:bg-surface"
              }`}
            >
              <div className={`w-1.5 h-1.5 rounded-full ${dotColors[cat] ?? "bg-gray-400"}`} />
              {labels[cat]}
            </button>
          ))}
        </div>
      </div>

      {/* Priorité */}
      <div>
        <span className="text-[10px] text-ink-faint uppercase tracking-wide mb-1.5 block">
          Priorité
        </span>
        <div className="flex gap-1.5">
          {(["urgent", "normal", "low"] as const).map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setSelectedPriority(p)}
              className={`px-2.5 py-1 text-[11px] font-medium rounded-md transition-colors ${
                selectedPriority === p
                  ? "bg-accent text-white"
                  : "text-ink-muted hover:text-ink hover:bg-surface"
              }`}
            >
              {PRIORITY_LABELS[p]}
            </button>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-2 justify-end">
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-2 text-xs font-medium border border-border/60 rounded-md text-ink hover:bg-surface-alt"
        >
          Annuler
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          className="px-3 py-2 text-xs font-medium bg-accent text-white rounded-md hover:opacity-90"
        >
          Ajouter
        </button>
      </div>
    </div>
  );
}

export function AddTaskInlineDialog({
  open,
  defaultCategory,
  onConfirm,
  onCancel,
}: AddTaskInlineDialogProps) {
  return (
    <Overlay open={open} onClose={onCancel}>
      <AddTaskInlineForm
        defaultCategory={defaultCategory}
        onConfirm={onConfirm}
        onCancel={onCancel}
      />
    </Overlay>
  );
}
